/**
 * Mock file download utilities for the Trust module.
 *
 * Since the frontend has no backend yet, these helpers generate plausible
 * content (PDF-like, CSV, JSON) and trigger browser downloads. Real
 * implementations will replace these helpers with API calls that stream the
 * actual stored file.
 */

export type DownloadPayload = {
  filename: string;
  content: string | Blob;
  mimeType: string;
};

/** Trigger a browser download for a text / binary blob. */
export function triggerDownload({ filename, content, mimeType }: DownloadPayload) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Generate a simple plain-text "PDF-looking" document (mock PDF). */
export function generateMockPdfContent(title: string, body: string): string {
  return `${title}
${'='.repeat(title.length)}

Generated: ${new Date().toISOString()}

${body}

---
[This is a mock document. In production, a real PDF is rendered.]
`;
}

/** Generate CSV from an array of rows. */
export function generateCsv(headers: string[], rows: Array<Array<string | number | null>>): string {
  const escape = (v: string | number | null) => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const lines: string[] = [];
  lines.push(headers.map(escape).join(','));
  rows.forEach(row => lines.push(row.map(escape).join(',')));
  return lines.join('\n');
}

// downloadAuditReport(input): builds + triggers a real download from live
// control+evidence data — excel→CSV, oscal→OSCAL assessment-results JSON,
// pdf→structured text artifact (watermark, sections). Returns the filename.
// (full builder authored above)

/** Shortcut: download an evidence file with mock content reflecting its metadata. */
export function downloadEvidence(evidence: {
  id: string;
  title: string;
  description: string;
  fileName: string | null;
  mimeType: string | null;
  type: string;
  source: string;
  versionNumber: number;
  uploadedAt: string;
  uploadedByName: string;
  controlMappings: Array<{ framework: string; controlId: string; controlName: string }>;
}) {
  const body = `Evidence ID: ${evidence.id}
Title: ${evidence.title}
Type: ${evidence.type}
Source: ${evidence.source}
Version: ${evidence.versionNumber}
Uploaded: ${evidence.uploadedAt}
Uploaded by: ${evidence.uploadedByName}

Description:
${evidence.description}

Control Mappings (${evidence.controlMappings.length}):
${evidence.controlMappings.map(m => `  - ${m.framework.toUpperCase()}: ${m.controlId} — ${m.controlName}`).join('\n')}
`;

  const filename = evidence.fileName ?? `${evidence.id}.txt`;
  const mimeType = evidence.mimeType ?? 'text/plain';

  // If the mime type is a real binary type we would normally stream, emit a readable placeholder.
  if (mimeType.startsWith('application/') || mimeType.startsWith('image/')) {
    triggerDownload({
      filename: filename.replace(/\.[^.]+$/, '') + '.placeholder.txt',
      content: generateMockPdfContent(evidence.title, body),
      mimeType: 'text/plain',
    });
  } else {
    triggerDownload({
      filename,
      content: body,
      mimeType,
    });
  }
}
